/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import clsx from 'clsx';
// Lib
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const PageTitle = (props)=>{
    const isEditing = useIsEditing();
    const styles = parseStyleParams(props.params, [
        'cta1'
    ]);
    const theme = props?.params?.selectTheme;
    const alignment = props?.params?.alignment || 'Left'; // Default: Left
    const { description, title, ctaLink } = props?.fields || {};
    const { page } = useSitecore();
    const currentPage = page?.layout?.sitecore?.route;
    const { pageTitle, pageDescription } = currentPage?.fields ?? {};
    const { base, cta, backgroundWrapper, contentWrapper, titleWrappers, headlineText, descriptionText, actionButtonWrapper } = TAILWIND_VARIANTS({
        alignment: alignment
    });
    /*
   * Rendering
   */ return /*#__PURE__*/ _jsx("section", {
        className: clsx(base(), styles),
        "data-component": "authorable/shared/content/pageTitle",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-page-title-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsxs(BrandAndThemeProvider, {
            theme: theme,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: clsx(backgroundWrapper()),
                    ...getTestProps(`background`)
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: contentWrapper(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: titleWrappers(),
                            children: [
                                /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: clsx(headlineText()),
                                    field: title,
                                    fallbacks: [
                                        pageTitle
                                    ],
                                    tag: "h1",
                                    ...getTestProps(`headline`)
                                }),
                                /*#__PURE__*/ _jsx(RichTextWrapper, {
                                    className: clsx(descriptionText()),
                                    field: description,
                                    fallbacks: [
                                        pageDescription
                                    ],
                                    ...getTestProps(`description`)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: actionButtonWrapper(),
                            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                ctaComponentClass: "component-section-button-color-1",
                                className: cta({
                                    style: styles.cta1?.ctaVariant
                                }),
                                ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                field: ctaLink,
                                ...getTestProps(`cta`)
                            })
                        })
                    ]
                })
            ]
        })
    });
};
export const Default = withStandardComponentWrapper(PageTitle);
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        style: 'primary'
    },
    slots: {
        cta: [],
        base: [
            'container',
            'max-w-none',
            'relative',
            'w-full',
            'py-layout-base-margin-y',
            'min-h-height-minHeight-header-minHeight'
        ],
        backgroundWrapper: [
            'absolute',
            'inset-0',
            'z-0',
            'left-[calc(-50vw+50%)]',
            'right-[calc(-50vw+50%)]',
            'bg-component-section-bg'
        ],
        contentWrapper: [
            'relative',
            'flex',
            'flex-col',
            'gap-general-spacing-copy-margin-bottom',
            'lg:max-w-typography-copy-max-Width'
        ],
        titleWrappers: [
            'flex',
            'flex-col',
            'gap-general-spacing-title-margin-bottom',
            'text-component-section-title'
        ],
        headlineText: [
            'text-typography-font-size-header-xlarge',
            'font-typography-font-family-header',
            'font-bold',
            'leading-normal',
            'text-component-section-title'
        ],
        descriptionText: [
            'font-typography-font-family-body',
            'font-normal',
            'leading-normal',
            'text-component-section-body',
            'text-typography-font-size-body-large'
        ],
        actionButtonWrapper: [
            'flex',
            'flex-col',
            'md:flex-row'
        ]
    },
    variants: {
        alignment: {
            Left: {
                contentWrapper: [
                    'justify-start',
                    'text-left'
                ],
                actionButtonWrapper: [
                    'justify-start'
                ],
                iconWrapper: [
                    'justify-start'
                ]
            },
            Center: {
                contentWrapper: [
                    'justify-center',
                    'text-center',
                    'm-auto'
                ],
                actionButtonWrapper: [
                    'justify-center'
                ],
                iconWrapper: [
                    'justify-center'
                ],
                descriptionText: [
                    'text-center'
                ]
            },
            Right: {
                contentWrapper: [
                    'justify-end',
                    'text-right',
                    'float-right'
                ],
                actionButtonWrapper: [
                    'justify-end'
                ],
                iconWrapper: [
                    'justify-end'
                ],
                descriptionText: [
                    'text-right'
                ]
            }
        },
        style: {
            tertiary: {
                cta: [
                    'flex',
                    'items-center',
                    'my-auto',
                    'gap-2',
                    'text-base',
                    // TODO: NEED TO UPDAT FIGMA TOKENS.
                    // This is a temporary fix to make the cta link themeable.
                    'text-component-accordion-toggle-active'
                ]
            },
            tertiaryInverse: {
                cta: [
                    'flex',
                    'items-center',
                    'my-auto',
                    'gap-2',
                    'text-base',
                    'text-component-accordion-toggle-active'
                ]
            },
            primary: {
                cta: [
                    'px-8'
                ]
            },
            secondary: {
                cta: [
                    'px-8'
                ]
            },
            primaryInverse: {
                cta: [
                    'px-8'
                ]
            },
            secondaryInverse: {
                cta: [
                    'px-8'
                ]
            },
            primaryOnCanvas: {
                cta: [
                    'px-8'
                ]
            },
            custom: {
                cta: []
            }
        }
    }
});
